import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Command } from 'pos/common';
import getBalanceIncident from './getBalanceIncident';

import { SessionProvider, handleServerErrors } from 'core/infrastructure';
import { Result } from 'core/infrastructure';
import { Res, PaymentMethodBalanceDTO, FlowAction } from 'core/api';
import { PosScreenClassComponent, DialogIcon, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import { TextBoxButton } from 'core/ui';
import { CashdrawerFacade } from 'pos/facades';

import { Balance } from 'pos/modules/helpers/cashdrawer';

import { CashControl } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { EditDenominationsScreen } from '../helpers/cashdrawer/EditDenominationsScreen';


type Props = { initialBalances: PaymentMethodBalanceDTO[] } & Modal.Actions<FlowAction>;
type State = {
	balances: PaymentMethodBalanceDTO[],
	denominations: Balance.Balance
};

async function editDenominations(balance: Balance.Balance): Promise<Result<Balance.Balance>> {
	return Modal.show(EditDenominationsScreen, { balance });
}

async function handleRequestErrors<T>(fn: () => Promise<T>): Promise<Result<T>> {
	return await handleServerErrors(fn, {
		onPrintError: async (printError) => {
			await Message.show(printError.message);
		}
	});
}
class MakeBalance extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<State>>();

	createInitialState(props: Props): Nullable<State> {
		return {
			balances: [...props.initialBalances],
			denominations: Balance.zero()
		};
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	protected getScreenId(): string {
		return 'MAKE_BALANCE';
	}

	protected getTitle(): string {
		return Res.Web.MAKE_BALANCE();
	}

	protected getImage(): string | SvgIcon {
		return Images.CloseDay;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			!SessionProvider.getCurrent().UseCashControl ? { title: Res.Web.OPEN_CASH_DRAWER_BTN(), image: Images.OpenCashDrawer, action: this.handleOpenCashdrawer } : null,
			{ title: Res.Web.PRINT_PARTIAL_BALANCE_BTN(), image: Images.PrintPartialBalance, action: this.handlePrintPartialBalance },
			{ title: Res.Web.PRINT_TOTAL_BALANCE_BTN(), image: Images.PrintTotalBalance, action: this.handlePrintTotalBalance },
			null,
			this.okButton(this.makeBalance)
		];
	}

	makeBalance = async () => {
		const valid = await this.form.current!.updateModel();
		if (!valid)
			return;

		const balances = this.state.balances;
		let incident = '';

		if (Balance.hasIncident(balances)) {
			const incidentResult = await getBalanceIncident(balances);
			if (!incidentResult.ok)
				return;
			incident = incidentResult.value;
		}

		const result = await handleRequestErrors(async () => await CashdrawerFacade.closeDay(balances, incident));
		if (!result.ok)
			return;

		this.close(FlowAction.ChangeUser);
	};

	makeCashBalance = async () => {
		const result = await editDenominations(this.state.denominations);
		if (result.ok) {
			const cashIndex = this.state.balances.findIndex(x => x.Method.IsCash);
			this.state.balances[cashIndex].ActualEndAmount = Balance.total(result.value);
			await this.form.current!.remount(`balances[${cashIndex}].ActualEndAmount`);
			this.setState({
				denominations: result.value
			});
		}
	};

	handleOpenCashdrawer = async () => {
		await Command.execute('openCashDrawer');
	};

	handlePrintPartialBalance = async () => await Command.execute('printPartialBalance');

	handlePrintTotalBalance = async () => await Command.execute('printTotalBalance');

	protected getContent(): React.ReactNode {
		const { Form, PriceBox } = UI.controlsFor<State>();

		return <Form ref={this.form} model={this.state}>
			<DialogIcon src={Images.CloseDay} />
			<div className='m-t-md m-l-md m-r-md'>
				{
					this.state.balances.map((balance, index) =>
						<PriceBox key={index} label={`${balance.Method.Name}:`} linkTo={`balances[${index}].ActualEndAmount`} disabled={SessionProvider.getCurrent().UseCashControl && balance.Method.IsCash} required allowNegative>
							{balance.Method.IsCash ? <TextBoxButton iconClassName='fa fa-edit' onClick={() => this.makeCashBalance()} /> : null}
						</PriceBox>)
				}
			</div>
		</Form>;
	}
}

async function closeWithCurrentBalances(balances: PaymentMethodBalanceDTO[]): Promise<Result<FlowAction>> {

	const { ok } = await Message.confirm(Res.Msg.MSG_CONFIRM_CLOSE_DAY_AFTER_PRINT_BALANCE());
	if (!ok)
		return Result.error();

	const fixedBalances = Balance.matchAllExpected(balances);
	await handleRequestErrors(async () => CashdrawerFacade.closeDay(fixedBalances, ''));
	return Result.ok(FlowAction.ChangeUser);
}

async function trySetActualAmountFromCashControl(balances: PaymentMethodBalanceDTO[]): Promise<boolean> {

	const result = await CashControl.getCurrentAmount();
	if (!result.ok) {
		await Message.show(Res.Msg.MSG_CANNOT_READ_CURRENT_AMOUNT_FROM_CASH_CONTROL());
		return false;
	}

	balances.find(x => x.Method.IsCash)!.ActualEndAmount = result.value;
	return true;
}

export default async function makeBalance(): Promise<Result<FlowAction>> {

	const balances = await CashdrawerFacade.getCurrentBalances();

	// Si hay CashControl, ajusto el efectivo al real del CashControl
	if (SessionProvider.getCurrent()!.UseCashControl && !(await trySetActualAmountFromCashControl(balances)))
		return Result.error();

	if (!SessionProvider.getCurrent().AllowCashTransactions || !SessionProvider.getCurrent().MakeBalanceOnCloseDay)
		return await closeWithCurrentBalances(balances);

	const result = await Modal.show(MakeBalance, { initialBalances: balances });
	if (result.ok && SessionProvider.getCurrent().UseCashControl) {
		await CashControl.closeDay();
	}

	return result;
}